import {
  ArchiveXIcon,
  FileIcon,
  InboxIcon,
  MailIcon,
  PaperclipIcon,
  SendIcon,
  Trash2Icon,
} from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import { Link, useSearchParams } from "react-router-dom"
import { FeatureOff } from "@/components/FeatureOff"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button, buttonVariants } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Label } from "@/components/ui/label"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import {
  folderCanFillToday,
  MAIL_FOLDERS,
  MAIL_FOLDER_LABEL_KEY,
  parseMailFolder,
  type MailFolder,
} from "@/features/mail/mail-folders"
import { isModuleOff, useMailbox, type MailMessageRow } from "@/features/mail/use-mailbox"
import { ApiError } from "@/lib/api-error"
import { istDayOfInstant, useFormatDay } from "@/lib/dates"
import { cn } from "@/lib/utils"
import { routes } from "@/routes/paths"

/**
 * The mailbox (§17A.1) — a folder list, the messages in the chosen folder, and
 * an unread toggle.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE LAYOUT IS `sidebar-09`'S SHAPE, BUILT FROM THIS APP'S OWN COMPONENTS, AND
 * IT IS NOT A `<Sidebar>`.
 *
 * The block puts both panes inside nested `Sidebar` elements. Doing that here
 * would be wrong twice over. First, mechanically: `SidebarProvider` is already
 * mounted once by `AppShell`, and a second `Sidebar` inside it fights the first
 * for the same context — the collapse state, the mobile Sheet, the ⌘B binding.
 * Second, and worse, `ui/sidebar.tsx` and `app-sidebar.tsx` are the app's
 * NAVIGATION, and this screen is CONTENT: it is reached through the rail and
 * lives beside it, never instead of it.
 *
 * So the two panes are two `Card`s in a flex row — the same construction every
 * other two-pane screen in this app uses. On a phone they stack, folders above
 * messages, which is what `md:flex-row` says and is why there is no second
 * mobile layout to keep in step (§11).
 *
 * DO NOT RUN `npx shadcn add sidebar-09` TO "FIX" THIS. That command overwrites
 * `app-sidebar.tsx`, `ui/sidebar.tsx` and `nav-user.tsx` with the block's own
 * versions; it has already destroyed the capability-gated navigation once, and
 * the settings menu had to be recovered from a build sourcemap. The block is a
 * reference for what the screen LOOKS like and nothing else.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * WHAT IT HONESTLY IS TODAY. `mail_messages` rows exist only for what this app
 * SENDS (§17A.2 stage 1). There is no IMAP sync, so Sent is the folder with
 * content and the other four are empty — permanently, until somebody builds the
 * sync. Every one of them says so in its own words rather than showing a blank
 * pane, because "nothing here" and "nothing can arrive here yet" are different
 * facts and the reader can act on only one of them.
 *
 * EVERY STRING IS RENDERED AS TEXT, NEVER AS HTML. Subjects, addresses and
 * previews arrive from outside this business — an inbound message's subject line
 * is written by whoever sent it. They go through JSX, which React escapes by
 * construction; there is no `dangerouslySetInnerHTML` in this feature and there
 * must never be one. `use-mailbox.ts` does not even carry `bodyHtml` on the row
 * shape, which is the stronger half of the same rule.
 */
export function MailboxScreen(): React.JSX.Element {
  const { t } = useTranslation()

  /*
   * THE FOLDER IS IN THE URL, THE TOGGLE IS NOT, AND THE SPLIT IS DELIBERATE.
   *
   * The folder is WHERE YOU ARE: `/mail?folder=SENT` survives a refresh, a back
   * button and a link sent from one phone to the other, and the folder rows are
   * real links so a middle click opens one in a tab. That is the same reasoning
   * `invoicesUnpaid` and `challansUnbilled` are query parameters rather than
   * component state.
   *
   * The unread toggle is HOW YOU ARE LOOKING at it — a momentary narrowing,
   * nobody bookmarks it, and putting it in the URL would push a history entry
   * every time somebody flicked it, so Back would walk through switch positions
   * instead of leaving the screen.
   */
  const [params] = useSearchParams()
  const folder = parseMailFolder(params.get("folder"))
  const [unreadOnly, setUnreadOnly] = React.useState(false)

  const query = useMailbox(folder, unreadOnly)
  const messages = query.data?.messages ?? []

  /*
   * §18.2 — THE 404 IS THE FLAG. There is no `useFlag` on the client (see
   * `use-mailbox.ts`), and a disabled module must read as "not switched on"
   * rather than as an empty mailbox, which would promise mail that cannot come.
   */
  if (query.isError && isModuleOff(query.error)) {
    return (
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <FeatureOff module={t("mail.title")} icon={<MailIcon />} />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">{t("mail.title")}</h1>
        <p className="text-muted-foreground text-sm">{t("mail.subtitle")}</p>
      </header>

      {query.isError && !isModuleOff(query.error) ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {query.error instanceof ApiError ? query.error.message : t("err.somethingWentWrong")}
            </span>
            <Button
              variant="outline"
              onClick={() => {
                void query.refetch()
              }}
            >
              {t("btn.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-col gap-4 md:flex-row md:items-start">
        <FolderList current={folder} />

        <Card className="flex min-w-0 flex-1 flex-col overflow-hidden py-0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
            <h2 className="text-base font-medium">{t(MAIL_FOLDER_LABEL_KEY[folder])}</h2>
            {/*
              * `sidebar-09`'s "Unreads" switch. It FILTERS and never writes:
              * there is no `mail.mark_read` capability to call (§2.1), so
              * nothing on this screen can change whether a message is read.
              */}
            <Label htmlFor="mail-unread-only" className="text-muted-foreground gap-2 text-sm">
              <span>{t("mail.unreadOnly")}</span>
              <Switch
                id="mail-unread-only"
                checked={unreadOnly}
                onCheckedChange={setUnreadOnly}
              />
            </Label>
          </div>

          <ScrollArea className="h-[60vh] min-h-64">
            {query.isPending ? <MessageListSkeleton /> : null}

            {/*
              * "THIS FOLDER IS EMPTY" IS A CLAIM ABOUT THE FOLDER, SO IT NEEDS
              * THE FOLDER — `query.data !== undefined`, the gate the messenger
              * and the notification hub both use. `isPending` is false in the
              * ERROR state too, so gating on it alone would put the red
              * "Couldn't load" banner and "Nothing has been emailed yet" on
              * screen together: the app asserting a folder is empty when the
              * truth is that it is unknown.
              */}
            {query.data !== undefined && messages.length === 0 ? (
              <EmptyFolder
                folder={folder}
                unreadOnly={unreadOnly}
                totalInFolder={query.data.total}
              />
            ) : null}

            {messages.length > 0 ? (
              <ul aria-label={t("mail.messages")} data-testid="mail-message-list">
                {messages.map((message) => (
                  <MessageRow key={message.id} message={message} folder={folder} />
                ))}
              </ul>
            ) : null}
          </ScrollArea>
        </Card>
      </div>
    </div>
  )
}

/** The icon for each folder — `sidebar-09`'s choices, from the same icon set. */
const FOLDER_ICON: Record<MailFolder, React.ReactNode> = {
  INBOX: <InboxIcon />,
  DRAFTS: <FileIcon />,
  SENT: <SendIcon />,
  SPAM: <ArchiveXIcon />,
  TRASH: <Trash2Icon />,
}

/**
 * The left pane.
 *
 * REAL LINKS, NOT BUTTONS THAT SET STATE. The block uses `onClick` and says so
 * ("IRL you should use the url/router"); this app takes it at its word. A link
 * can be middle-clicked, bookmarked and sent, and `aria-current="page"` tells a
 * screen reader which folder is open — none of which a `<button>` gives for
 * free.
 *
 * NO PER-FOLDER COUNTS. The list would need a count for every folder, which is
 * five queries or an endpoint that returns five numbers — for four folders that
 * are structurally empty. A row reading "Inbox 0" repeated four times is how a
 * screen teaches somebody that its numbers are decoration.
 */
function FolderList({ current }: { current: MailFolder }): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <Card className="shrink-0 py-2 md:w-56">
      <nav aria-label={t("mail.folders")}>
        <ul className="flex flex-row gap-1 overflow-x-auto px-2 md:flex-col md:overflow-visible">
          {MAIL_FOLDERS.map((folder) => {
            const active = folder === current
            return (
              <li key={folder}>
                {/*
                  * A PLAIN `Link` WEARING `buttonVariants`, not a `Button` with
                  * a `render` prop. The Base UI button primitive gives a
                  * non-native element `role="button"`, which is correct for a
                  * control and wrong here: this navigates, so it must be
                  * ANNOUNCED as a link and offered the browser's link
                  * affordances. `calendar.tsx` styles its day cells the same
                  * way, for the same reason.
                  */}
                <Link
                  to={routes.mailFolder(folder)}
                  className={cn(
                    buttonVariants({ variant: active ? "secondary" : "ghost" }),
                    "w-full justify-start gap-2",
                  )}
                  aria-current={active ? "page" : undefined}
                >
                  {FOLDER_ICON[folder]}
                  <span>{t(MAIL_FOLDER_LABEL_KEY[folder])}</span>
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>
    </Card>
  )
}

/**
 * An empty folder, saying WHICH KIND of empty it is.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THREE SENTENCES, NOT ONE, BECAUSE THREE DIFFERENT THINGS ARE TRUE.
 *
 *  1. THE FILTER IS ON AND HID EVERYTHING. The folder has messages; the reader
 *     narrowed to unread and none are. Saying "nothing has been emailed yet"
 *     here would be flatly false, and the reader would go looking for mail that
 *     is on screen the moment the switch goes off.
 *  2. SENT IS GENUINELY EMPTY. Nothing has gone out yet — a state the owner can
 *     change himself, from a document's Share menu, so the sentence says where.
 *  3. INBOX, DRAFTS, JUNK, TRASH. Empty because NOTHING FILLS THEM in this
 *     build: SteelBill does not collect incoming mail. Telling the owner to wait
 *     would be telling him to wait for something that cannot arrive, and he
 *     would conclude the app was broken rather than incomplete — which is
 *     exactly the reading `FeatureOff` exists to prevent for switched-off
 *     modules, applied here to an unbuilt half of a switched-on one.
 * ═════════════════════════════════════════════════════════════════════════════
 */
function EmptyFolder({
  folder,
  unreadOnly,
  totalInFolder,
}: {
  folder: MailFolder
  unreadOnly: boolean
  totalInFolder: number
}): React.JSX.Element {
  const { t } = useTranslation()

  const state =
    unreadOnly && totalInFolder > 0
      ? "unread"
      : folderCanFillToday(folder)
        ? "sent"
        : "incoming"

  const copy = {
    unread: { title: "mail.emptyUnreadTitle", help: "mail.emptyUnreadHelp" },
    sent: { title: "mail.emptySentTitle", help: "mail.emptySentHelp" },
    incoming: { title: "mail.emptyIncomingTitle", help: "mail.emptyIncomingHelp" },
  }[state]

  return (
    <Empty className="border-0 py-12" data-testid={`mail-empty-${state}`}>
      <EmptyHeader>
        <EmptyMedia variant="icon">{FOLDER_ICON[folder]}</EmptyMedia>
        <EmptyTitle>{t(copy.title)}</EmptyTitle>
        <EmptyDescription>{t(copy.help)}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  )
}

/**
 * One message.
 *
 * WHOSE ADDRESS IS SHOWN DEPENDS ON THE FOLDER, and getting this wrong would
 * make the whole screen useless: every row in Sent has the SAME sender — this
 * business — so a Sent list showing "From" would be the company's own address
 * printed forty times. The question the owner has is "who did this go to", so
 * outbound folders show the recipient and inbound ones show the sender.
 */
function MessageRow({
  message,
  folder,
}: {
  message: MailMessageRow
  folder: MailFolder
}): React.JSX.Element {
  const { t } = useTranslation()
  const formatDay = useFormatDay()

  const outbound = folder === "SENT" || folder === "DRAFTS"
  const party = outbound
    ? t("mail.to", { address: message.toAddresses })
    : message.fromName ?? message.fromAddress

  /*
   * `istDayOfInstant` FIRST, `formatDay` SECOND — every time. Both columns are
   * ISO instants, and slicing one to ten characters takes the UTC day and prints
   * it as an IST one, so anything sent between 00:00 and 05:30 IST reports
   * yesterday. The owner works late. A draft has neither timestamp, which is a
   * blank cell rather than an invented one.
   */
  const instant = message.sentAt ?? message.receivedAt
  const day = istDayOfInstant(instant)

  return (
    <li className="border-b last:border-b-0" data-testid="mail-message-row">
      <article className="flex flex-col gap-1 p-4 text-sm">
        <div className="flex w-full items-center gap-2">
          {!message.isRead ? (
            <span className="bg-primary size-2 shrink-0 rounded-full" aria-hidden="true" />
          ) : null}
          <span className="truncate font-medium">{party}</span>
          {!message.isRead ? <span className="sr-only">{t("mail.unread")}</span> : null}
          {message.hasAttachments ? (
            <>
              <PaperclipIcon className="size-3.5 shrink-0" aria-hidden="true" />
              <span className="sr-only">{t("mail.hasAttachment")}</span>
            </>
          ) : null}
          {day === null ? null : (
            <span className="text-muted-foreground ml-auto shrink-0 text-xs">
              {formatDay(day)}
            </span>
          )}
        </div>

        <span className="truncate font-medium">
          {message.subject ?? t("mail.noSubject")}
        </span>

        {message.preview === null ? null : (
          <span className="text-muted-foreground line-clamp-2 text-xs">{message.preview}</span>
        )}
      </article>
    </li>
  )
}

function MessageListSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col">
      {[0, 1, 2].map((n) => (
        <div key={n} className="flex flex-col gap-2 border-b p-4 last:border-b-0">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-3 w-48" />
          <Skeleton className="h-3 w-full max-w-sm" />
        </div>
      ))}
    </div>
  )
}
